'use client';
import React, { HTMLProps } from 'react';

import { cn } from '@/lib/utils';

import { GOOGLE_SCHEMA, SCHEMA_LINKS } from '@/constant/schemas';

import { useAccordion } from './AccordionProvider';

import Button from '@/decodo/components/atoms/Button';
import IconChevronDown from '~/svg/IconChevronDown.svg';

export type AccordionItemProps = {
  children: React.ReactNode;
  header: React.ReactNode;
  id: string;
  enableGoogleSchema?: boolean;
  classNames?: {
    wrapper?: string;
    headerWrapper?: string;
    icon?: string;
    contentWrapper?: string;
    content?: string;
  };
};

type SchemaProps = Pick<
  HTMLProps<HTMLDivElement>,
  'itemScope' | 'itemProp' | 'itemType'
>;

export default function AccordionItem({
  children,
  header,
  id,
  enableGoogleSchema,
  classNames,
}: AccordionItemProps) {
  const { getIsItemOpen, toggleItemId } = useAccordion();

  const isOpen = getIsItemOpen(id);

  function handleToggle() {
    toggleItemId(id);
  }

  const schemaWrapperProps: SchemaProps = enableGoogleSchema
    ? {
        itemScope: true,
        itemProp: GOOGLE_SCHEMA.entity,
        itemType: SCHEMA_LINKS.QUESTION,
      }
    : {};

  const schemaContentWrapperProps: SchemaProps = enableGoogleSchema
    ? {
        itemScope: true,
        itemType: SCHEMA_LINKS.ANSWER,
        itemProp: GOOGLE_SCHEMA.answers,
      }
    : {};

  const schemaContentProps: SchemaProps = enableGoogleSchema
    ? { itemProp: GOOGLE_SCHEMA.text }
    : {};

  const schemaHeaderWrapperProps: SchemaProps = enableGoogleSchema
    ? {
        itemProp: GOOGLE_SCHEMA.name,
      }
    : {};

  return (
    <div
      className={cn(
        'flex flex-col border-t border-solid border-gray-200 dark:border-gray-300',
        classNames?.wrapper
      )}
      {...schemaWrapperProps}
    >
      <div
        onClick={handleToggle}
        className={cn(
          'text-subtitle-l-d flex cursor-pointer items-center justify-between py-6 font-serif text-gray-950',
          classNames?.headerWrapper
        )}
        role='button'
        data-tracker-component={`accordion-item-unfold-${id}`}
        {...schemaHeaderWrapperProps}
      >
        {header}
        <Button noStyles>
          <IconChevronDown
            className={cn(
              'min-h-[1.5rem] min-w-[1.5rem] text-gray-950 transition-all',
              isOpen && 'rotate-180',
              classNames?.icon
            )}
            data-tracker-component={`accordion-item-unfold-chevron-${id}`}
          />
        </Button>
      </div>
      <div
        className={cn(
          isOpen ? 'grid-rows-[1fr] pb-6' : 'grid-rows-[0fr]',
          'grid transition-all duration-300 ease-out',
          classNames?.contentWrapper
        )}
        {...schemaContentWrapperProps}
      >
        <div
          className={cn('overflow-hidden text-gray-500', classNames?.content)}
          {...schemaContentProps}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
